<template>
  <div class="super-admin-markets">
    <!-- Header -->
    <header class="page-header">
      <h1>Markets</h1>
      <div class="header-actions">
        <button
            v-if="!drawing_for"
            type="button"
            class="btn ghost"
            @click="toggleSummaryMap"
        >{{ show_map ? 'Hide map' : 'Show map' }}</button>
        <button
            v-if="!drawing_for"
            type="button"
            class="btn ghost"
            @click="forecast_open = true"
        >Forecasting</button>
        <button type="button" class="btn primary" @click="add_market_open = true">
          + Add Market
        </button>
      </div>
    </header>

    <!-- Reserved-territories view map. Reads from `GET /v1/markets` (same
         endpoint kiey.com/my-market uses) via the shared Vuex store, so
         the polygons + labels here always match what the public map
         shows. Drawing happens in the MarketSelection fullscreen overlay
         below when `drawing_for` is set. -->
    <!-- Map area: in normal browse, the summary map is OFF by default —
         it's the slowest thing on the page (Google Maps SDK + polygon
         render for every reserved territory). User opts in via Show map.
         While granting/editing a market, this slot hosts the
         MarketSelection drawing UI inline regardless. -->
    <div
        v-if="(drawing_for && !grant_pending) || show_map"
        class="map-wrap"
        :class="{ 'is-drawing': drawing_for && !grant_pending }"
    >
      <template v-if="drawing_for && !grant_pending">
        <MarketSelection
            :admin-mode="true"
            :admin-tenant-name="drawing_for.name || ''"
            :admin-initial-zips="drawing_for.initial_zips || []"
            :admin-edit-tenant-id="drawing_for.mode === 'edit' ? drawing_for.tenant_id : ''"
            @done="onDrawingDone"
            @cancel="exitDrawing"
        />
      </template>
      <template v-else>
        <GmapMap
            v-if="map_ready"
            ref="mapRef"
            :center="map_center"
            :zoom="map_zoom"
            :options="map_options"
            class="map"
        >
          <GmapMarker
              v-for="(m, ownerId) in reservedAsPins"
              :key="'admin-pin-' + ownerId"
              :position="hullCentroid(m.hull)"
              :icon="reservedPinIcon"
              @click="onPolygonClick(ownerId)"
          />
          <GmapPolygon
              v-for="(m, ownerId) in reservedAsPolygons"
              :key="'admin-poly-' + ownerId"
              :paths="m.hull"
              :options="polygonOptionsFor(ownerId, m)"
              @click="onPolygonClick(ownerId)"
          />
          <MarketPolygonLabel
              v-for="(m, ownerId) in reservedAsPolygons"
              :key="'admin-label-' + ownerId"
              :position="hullCentroid(m.hull)"
              :pricing-mode="m.pricing_mode"
              :owner-name="m.owner_name || ''"
              :current-zoom="map_current_zoom"
              :label-zoom="m.label_zoom"
          />
        </GmapMap>
        <div v-else class="map map-placeholder">Loading map…</div>
        <div class="map-legend">
          <span class="legend-item"><span class="swatch sw-paid"></span>Paid</span>
          <span class="legend-item"><span class="swatch sw-free"></span>Free</span>
          <span class="legend-item"><span class="swatch sw-selected"></span>Selected</span>
        </div>
      </template>
    </div>

    <!-- Address exclusivity checker — opens as a centered modal overlay so the
         input + suggestions always have room (no top-of-page dropdown flip). -->
    <div v-if="!drawing_for || grant_pending" class="addr-check">
      <button type="button" class="addr-check-toggle" @click="openAddressCheck">
        <span class="addr-check-pin">📍</span>
        <span>Check an address</span>
        <span class="muted small addr-check-hint">Is it inside an owner’s exclusive market?</span>
      </button>
    </div>

    <!-- Centered modal popup -->
    <div v-if="addr_open" class="addr-modal-backdrop" @click.self="closeAddressCheck">
      <div class="addr-modal" role="dialog" aria-modal="true" aria-label="Check an address">
        <div class="addr-modal-head">
          <h3 class="addr-modal-title">Check an address</h3>
          <button type="button" class="addr-modal-close" aria-label="Close" @click="closeAddressCheck">×</button>
        </div>
        <p class="addr-modal-sub">See whether an address falls inside an owner’s exclusive market — and which owner is nearest if not.</p>

        <div class="addr-check-row">
          <div class="addr-check-input-wrap">
            <input
                ref="addrCheckInput"
                v-model="addr_query"
                type="text"
                class="search-input addr-check-input"
                placeholder="Type or paste an address, then Check…"
                autocomplete="off"
                @input="onAddrInput"
                @focus="addr_suggest_open = true"
                @blur="onAddrBlur"
                @keyup.enter="runAddressCheck"
            />
            <!-- Our OWN suggestions list, in normal flow below the input inside
                 the modal — never Google's body-appended .pac-container. -->
            <ul v-if="addr_suggest_open && addr_suggestions.length" class="addr-suggest">
              <li
                  v-for="s in addr_suggestions"
                  :key="s.place_id"
                  class="addr-suggest-item"
                  @mousedown.prevent="selectSuggestion(s)"
              >
                <span class="addr-suggest-main">{{ s.main }}</span>
                <span v-if="s.secondary" class="addr-suggest-sec">{{ s.secondary }}</span>
              </li>
              <li class="addr-suggest-credit">powered by Google</li>
            </ul>
          </div>
          <button
              type="button"
              class="addr-check-btn"
              :disabled="addr_checking || !(addr_query && addr_query.trim())"
              @click="runAddressCheck"
          >{{ addr_checking ? 'Checking…' : 'Check' }}</button>
          <button
              v-if="addr_result || addr_error || addr_query"
              type="button"
              class="addr-check-clear"
              @click="clearAddressCheck"
          >Clear</button>
        </div>

        <p v-if="addr_error" class="addr-check-error">{{ addr_error }}</p>

        <div v-if="addr_result" class="addr-check-result">
          <span v-if="addr_result.in_exclusive" class="addr-badge addr-badge-yes">✓ Inside an exclusive market</span>
          <span v-else class="addr-badge addr-badge-no">✗ Not in any exclusive market</span>

          <div v-if="addr_result.in_exclusive" class="addr-detail">
            <strong>{{ (addr_result.owner && addr_result.owner.name) || 'Unknown owner' }}</strong>
            owns ZIP {{ addr_result.owner && addr_result.owner.zip }}.
          </div>
          <div v-else class="addr-detail">
            <template v-if="addr_result.nearest">
              Nearest market: <strong>{{ addr_result.nearest.name || 'Unknown owner' }}</strong>
              — ~{{ addr_result.nearest.distance_miles }} mi away
              <span class="muted">(nearest ZIP {{ addr_result.nearest.zip }})</span>
            </template>
            <template v-else>No reserved markets to compare against.</template>
          </div>

          <p v-if="!addr_result.checked_zip" class="addr-check-warn">
            Google didn’t return a ZIP for this place, so the yes/no falls back to the
            nearest market — pick a more specific street address for an exact answer.
          </p>
          <p class="addr-check-foot muted small">Distance is approximate (to the nearest owned ZIP centroid).</p>
        </div>
      </div>
    </div>

    <!-- Table — hidden while the drawing UI is active so the user has
         full room to work, with the page header staying visible above. -->
    <div v-if="!drawing_for || grant_pending" class="table-toolbar">
      <input
          v-model="search"
          type="text"
          class="search-input"
          placeholder="Search by name, email, phone, ZIP, mode, status…"
      />
      <span v-if="search" class="muted small">
        {{ filteredRows.length }} of {{ rows.length }}
      </span>
      <button
          v-if="filteredRows.length"
          type="button"
          class="csv-download-btn"
          @click="downloadCsv"
      >Download CSV</button>
    </div>
    <div v-if="!drawing_for || grant_pending" class="table-wrap">
      <div v-if="loading_rows && !rows.length" class="muted small pad">Loading tenant details…</div>
      <div v-else-if="!filteredRows.length" class="muted small pad">
        {{ rows.length ? 'No rows match the filter.' : 'No reserved markets yet. Use “+ Add Market” to grant one.' }}
      </div>
      <table v-else class="markets-table">
        <thead>
          <tr>
            <th
                v-for="col in columns"
                :key="col.key"
                :class="[col.numeric ? 'num' : '', { sortable: col.sortable, sorted: sort_key === col.key }]"
            >
              <span @click="col.sortable && toggleSort(col.key)" :style="col.sortable ? 'cursor: pointer;' : ''">
                {{ col.label }}
              </span>
              <span
                  v-if="col.key === 'pricing_mode'"
                  class="th-filter-trigger"
                  :class="{ active: mode_filter.length }"
                  @click.stop="toggleModeFilter"
                  :title="mode_filter.length ? `Filter: ${mode_filter.join(', ')}` : 'Filter by mode'"
              >▾</span>
              <div v-if="col.key === 'pricing_mode' && mode_filter_open" class="th-filter-popover" @click.stop>
                <label v-for="opt in mode_filter_options" :key="opt" class="th-filter-option">
                  <input type="checkbox" :value="opt" v-model="mode_filter" />
                  {{ pricingModeLabel(opt) }}
                </label>
                <div class="th-filter-actions">
                  <button type="button" class="link-btn" @click="mode_filter = []">Clear</button>
                  <button type="button" class="link-btn" @click="mode_filter_open = false">Done</button>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
              v-for="row in sortedRows"
              :key="row.tenant_id"
              :class="{ selected: row.tenant_id === selected_owner_id }"
              @click="openDetail(row)"
          >
            <td><strong>{{ row.owner_display_name || '—' }}</strong></td>
            <td>{{ row.email || '—' }}</td>
            <td>{{ row.address || '—' }}</td>
            <td>{{ row.city_state || '—' }}</td>
            <td class="num">~{{ formatNumber(row.market_population) }}</td>
            <td class="num">{{ row.radius_miles ? `${row.radius_miles} mi` : '—' }}</td>
            <td class="num">{{ teamTotal(row.team) }}</td>
            <td>
              <div class="mode-cell">
                <span class="badge" :class="modeBadgeClass(row.pricing_mode)">
                  {{ pricingModeLabel(row.pricing_mode) }}
                </span>
                <span v-if="row.coupon" class="badge badge-coupon" :title="couponTooltip(row.coupon)">
                  {{ row.coupon.id }}
                </span>
              </div>
            </td>
            <td class="num">${{ formatNumber(row.monthly_price_effective) }}</td>
            <td>{{ nextPaymentLabel(row) }}</td>
            <td>{{ formatDate(row.signup_date) }}</td>
            <td>{{ statusLabel(row.status) }}</td>
          </tr>
        </tbody>
        <tfoot v-if="filteredRows.length">
          <tr class="markets-total-row">
            <td class="markets-total-label"><strong>Totals ({{ filteredRows.length }})</strong></td>
            <td colspan="3"></td>
            <td class="num"><strong>~{{ formatNumber(totalPopulation) }}</strong></td>
            <td></td>
            <td class="num"><strong>{{ formatNumber(totalUsers) }}</strong></td>
            <td></td>
            <td class="num"><strong>${{ formatNumber(totalMonthly) }}/mo</strong></td>
            <td colspan="3"></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Slide-out detail drawer -->
    <div
        v-if="detail_open"
        class="drawer-scrim"
        @click.self="detail_open = false"
    ></div>
    <aside class="drawer" :class="{ open: detail_open }">
      <div v-if="detail_loading && !detail" class="drawer-content">
        <header class="drawer-header">
          <h2>Loading…</h2>
          <button type="button" class="close-x" @click="closeDetail" aria-label="Close">×</button>
        </header>
        <p class="muted small">Fetching tenant details…</p>
      </div>
      <div v-else-if="detail_error && !detail" class="drawer-content">
        <header class="drawer-header">
          <h2>Couldn't load</h2>
          <button type="button" class="close-x" @click="closeDetail" aria-label="Close">×</button>
        </header>
        <p class="muted small">{{ detail_error }}</p>
        <p class="muted small">
          The selected row references tenant id <code>{{ selected_owner_id }}</code>,
          which doesn't exist in the tenants collection. This happens with the
          demo seed markets.
        </p>
      </div>
      <div v-else-if="detail" class="drawer-content">
        <header class="drawer-header">
          <h2>{{ detail.tenant_name || (detail.owner && detail.owner.name) || '—' }}</h2>
          <button type="button" class="close-x" @click="closeDetail" aria-label="Close">×</button>
        </header>

        <section class="detail-section">
          <h3>Owner</h3>
          <div class="kv"><span>Email</span><span>{{ detail.email || '—' }}</span></div>
          <div class="kv"><span>Phone</span><span>{{ detail.phone || '—' }}</span></div>
          <div class="kv kv-editable">
            <span>Address</span>
            <span v-if="!editing_address" class="kv-value-row">
              <span>{{ detail.address || '—' }}</span>
              <button type="button" class="link-btn" @click="startEditAddress">{{ detail.address ? 'Edit' : 'Add' }}</button>
            </span>
            <span v-else class="kv-edit-row">
              <input
                  ref="addressInput"
                  v-model="address_draft"
                  type="text"
                  class="inline-input"
                  placeholder="Start typing an address…"
                  autocomplete="off"
                  @keydown.enter="saveAddress"
                  @keydown.escape="cancelEditAddress"
              />
              <button type="button" class="link-btn" :disabled="address_saving" @click="saveAddress">
                {{ address_saving ? 'Saving…' : 'Save' }}
              </button>
              <button type="button" class="link-btn muted" @click="cancelEditAddress">Cancel</button>
              <span v-if="address_error" class="kv-error">{{ address_error }}</span>
            </span>
          </div>
          <div class="kv"><span>Tenant ID</span><span class="mono">{{ detail.tenant_id }}</span></div>
          <div class="kv"><span>Signed up</span><span>{{ formatDate(detail.signup_date) }}</span></div>
          <div class="kv"><span>Heard from</span><span>{{ detail.signup_heard_from || '—' }}</span></div>
          <div v-if="detail.owner && detail.owner.last_login_at" class="kv">
            <span>Last login</span><span>{{ formatDate(detail.owner.last_login_at) }}</span>
          </div>
        </section>

        <section class="detail-section">
          <h3>Market</h3>
          <div class="kv"><span>Center</span><span>{{ marketCenter || '—' }}</span></div>
          <div class="kv"><span>States covered</span><span>{{ marketStatesCovered || '—' }}</span></div>
          <div class="kv"><span>Top cities</span><span>{{ marketTopCities || '—' }}</span></div>
          <div class="kv"><span>Span</span><span>{{ marketSpanMiles ? `~${marketSpanMiles} mi` : '—' }}</span></div>
          <div class="kv"><span>ZIPs</span><span>{{ detail.zips.length }}</span></div>
          <div class="kv"><span>Population</span><span>~{{ formatNumber(detail.market_population) }}</span></div>
          <div v-if="marketGrantedAt" class="kv"><span>Granted</span><span>{{ formatDate(marketGrantedAt) }}<span v-if="marketGrantedBy"> · {{ marketGrantedBy }}</span></span></div>
          <div v-if="marketLastEditedAt" class="kv"><span>Last edited</span><span>{{ formatDate(marketLastEditedAt) }}<span v-if="marketLastEditedBy"> · {{ marketLastEditedBy }}</span></span></div>
          <button type="button" class="btn outline mt-1" @click="startEditingFromDrawer">
            Edit Market
          </button>
          <button type="button" class="btn danger mt-1" @click="askDeleteFromDrawer">
            Delete Market
          </button>
        </section>

        <section class="detail-section">
          <h3>Pricing</h3>
          <div class="kv">
            <span>Mode</span>
            <span>
              {{ pricingModeLabel(detail.pricing_mode) }}
              <span v-if="detail.pricing_mode === 'free'" class="badge badge-free">Free</span>
            </span>
          </div>
          <div class="kv"><span>Calculated</span><span>${{ formatNumber(detail.monthly_price_calculated) }} / mo</span></div>
          <div class="kv"><span>Effective</span><span><strong>${{ formatNumber(detail.monthly_price_effective) }} / mo</strong></span></div>
          <div class="kv">
            <span>Coupon</span>
            <span>
              <template v-if="detail.coupon">
                <span class="badge badge-coupon">{{ detail.coupon.id }}</span>
                <span class="muted small"> {{ couponTooltip(detail.coupon) }}</span>
                <button type="button" class="link-btn ml-2" @click="onChangeCoupon">Change</button>
                <button type="button" class="link-btn" @click="onRemoveCoupon">Remove</button>
              </template>
              <template v-else>
                <span class="muted small">None</span>
                <button type="button" class="link-btn ml-2" @click="onChangeCoupon">Add coupon</button>
              </template>
            </span>
          </div>
          <div v-if="detail.next_renewal_at || detail.stripe_subscription_id" class="kv">
            <span>Next payment</span>
            <span>
              {{ formatDate(detail.next_renewal_at) }}
              <button type="button" class="link-btn ml-2" @click="onChangeBillingDate">Change</button>
            </span>
          </div>
          <div v-if="pricingNote" class="kv kv-note">
            <span>Note</span>
            <span class="note-text">{{ pricingNote.text }}<span v-if="pricingNote.by" class="note-by"> · {{ pricingNote.by }} · {{ formatDate(pricingNote.ts) }}</span></span>
          </div>
          <button type="button" class="btn outline mt-1" @click="override_price_open = true">
            Override Price
          </button>
        </section>

        <section class="detail-section">
          <h3>Team</h3>
          <div class="kv"><span>Agents</span><span>{{ detail.team.agents }}</span></div>
          <div class="kv"><span>Vendors</span><span>{{ detail.team.vendors }}</span></div>
          <div class="kv"><span>Employees</span><span>{{ detail.team.employees }}</span></div>
          <div class="kv"><span>Homeowners</span><span>{{ detail.team.homeowners }}</span></div>
          <div class="kv"><span>Total</span><span><strong>{{ detail.team.total }}</strong></span></div>
        </section>

        <section class="detail-section">
          <h3>Audit Log</h3>
          <div v-if="!detail.audit.length" class="muted">No actions recorded.</div>
          <ul class="audit-list">
            <li v-for="row in detail.audit" :key="row.id">
              <div class="audit-meta">
                <span class="mono">{{ formatDate(row.ts) }}</span>
                <span>·</span>
                <span>{{ row.super_user_email || row.super_user_phone || '—' }}</span>
                <span>·</span>
                <strong>{{ row.action }}</strong>
              </div>
              <div v-if="row.reason" class="audit-reason">{{ row.reason }}</div>
            </li>
          </ul>
        </section>
      </div>
    </aside>

    <OverridePriceModal
        v-if="detail && override_price_open"
        :show.sync="override_price_open"
        :tenant-id="detail.tenant_id"
        :tenant-name="detail.tenant_name || (detail.owner && detail.owner.name) || ''"
        :pricing-mode="detail.pricing_mode"
        :monthly-price-calculated="detail.monthly_price_calculated"
        :monthly-price-override="detail.monthly_price_override"
        @saved="onPriceSaved"
    />

    <AddMarketModal
        v-if="add_market_open"
        :show.sync="add_market_open"
        @select-tenant="onTenantSelected"
    />

    <ForecastModal
        v-if="forecast_open"
        :show.sync="forecast_open"
        :rows="rows"
    />

    <!-- Delete-confirm dialog -->
    <div v-if="delete_pending" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal-card small" role="dialog" aria-modal="true">
        <header class="modal-header">
          <h2>Delete market — {{ delete_pending.name }}</h2>
        </header>
        <div class="modal-body">
          <p>Releases <strong>{{ delete_pending.zip_count }}</strong> ZIP{{ delete_pending.zip_count === 1 ? '' : 's' }} back to available, clears cached pricing on this tenant, and removes them from the public map. This is reversible (you can re-grant) but the audit row stays.</p>
          <label class="field">
            <span>Reason (optional)</span>
            <textarea v-model="delete_reason" rows="2"></textarea>
          </label>
          <div v-if="delete_error" class="alert">{{ delete_error }}</div>
        </div>
        <footer class="modal-footer">
          <button type="button" class="btn ghost" :disabled="deleting" @click="cancelDelete">Cancel</button>
          <button type="button" class="btn danger" :disabled="deleting" @click="confirmDelete">
            {{ deleting ? 'Deleting…' : 'Delete market' }}
          </button>
        </footer>
      </div>
    </div>

    <!-- Final confirm after drawing completes: capture pricing mode, optional
         override amount + reason, then commit (grant for create, set-zips
         for edit). Pricing is set inline so the super-user doesn't have to
         do a second pass via the detail drawer's Override Price modal. -->
    <div v-if="grant_pending" class="modal-overlay" @click.self="cancelGrant">
      <div class="modal-card" role="dialog" aria-modal="true">
        <header class="modal-header">
          <h2>{{ grant_pending.mode === 'edit' ? 'Save market' : 'Grant market' }} — {{ grant_pending.name }}</h2>
        </header>
        <div class="modal-body">
          <ul class="summary">
            <li><span>ZIPs</span><span>{{ grant_pending.zips.length }}</span></li>
            <li><span>Population</span><span>~{{ formatNumber(grant_pending.population) }}</span></li>
            <li><span>Calculated price</span><span>${{ formatNumber(grant_pending.calculated) }} / month</span></li>
          </ul>

          <div class="modes">
            <div class="modes-label">Pricing</div>
            <label class="mode">
              <input type="radio" v-model="grant_pricing_mode" value="calculated" />
              <div>
                <div class="mode-title">Calculated</div>
                <div class="mode-detail">Bill ${{ formatNumber(grant_pending.calculated) }} / month — derived from population.</div>
              </div>
            </label>
            <label class="mode">
              <input type="radio" v-model="grant_pricing_mode" value="free" />
              <div>
                <div class="mode-title">Free</div>
                <div class="mode-detail">$0 / month. Polygon will show this tenant's name on the public map.</div>
              </div>
            </label>
            <label class="mode">
              <input type="radio" v-model="grant_pricing_mode" value="custom" />
              <div>
                <div class="mode-title">Custom</div>
                <div class="mode-detail">
                  Manual amount.
                  <span v-if="grant_pricing_mode === 'custom'" class="custom-input-wrap">
                    <span class="custom-prefix">$</span>
                    <input
                        type="number"
                        v-model.number="grant_custom_amount"
                        class="custom-input"
                        min="0"
                    />
                    <span class="custom-suffix">/ month</span>
                  </span>
                </div>
              </div>
            </label>
          </div>

          <label class="field">
            <span>Reason (optional)</span>
            <textarea v-model="grant_reason" rows="2"></textarea>
          </label>
          <div v-if="grant_error" class="alert">{{ grant_error }}</div>
        </div>
        <footer class="modal-footer">
          <button type="button" class="btn ghost" :disabled="granting" @click="cancelGrant">Back to drawing</button>
          <button type="button" class="btn primary" :disabled="granting || !grantValid" @click="confirmGrant">
            {{ granting ? 'Saving…' : (grant_pending.mode === 'edit' ? 'Save changes' : 'Confirm grant') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { gmapApi, Marker as GmapMarker, Polygon as GmapPolygon } from 'vue2-google-maps';
import MarketPolygonLabel from '@/components/markets/MarketPolygonLabel.vue';
// Lazy-load the heavy child components: MarketSelection alone is 2K lines
// + brings in vue2-google-maps + Vuetify chrome — bundling it eagerly
// makes the markets-route chunk far bigger than it needs to be on first
// paint. These imports resolve to webpack chunks only when the modal /
// drawing UI actually renders.
const OverridePriceModal = () => import(/* webpackChunkName: "markets-pricemodal" */ './OverridePriceModal.vue');
const AddMarketModal = () => import(/* webpackChunkName: "markets-pickermodal" */ './AddMarketModal.vue');
const ForecastModal = () => import(/* webpackChunkName: "markets-forecastmodal" */ './ForecastModal.vue');
const MarketSelection = () => import(/* webpackChunkName: "market-selection" */ '@/views/owner/sign_up/market_selection.vue');
import { listMarkets, getMarketDetail, grantMarket, setMarketZips, setMarketPrice, setMarketBillingDate, setMarketCoupon, releaseMarket, updateTenantAddress, checkAddress, humanizeAxiosError } from './markets_api';

const PAID_BORDER = '#7E0F0F';
const PAID_FILL = '#FBE4E4';
const FREE_BORDER = '#0B5D55';
const FREE_FILL = '#D2F0EC';
const SELECTED_FILL = '#FFE9B8';

export default {
  name: 'SuperAdminMarkets',
  components: {
    GmapMarker,
    GmapPolygon,
    MarketPolygonLabel,
    OverridePriceModal,
    AddMarketModal,
    ForecastModal,
    MarketSelection,
  },
  data() {
    return {
      loading: false,
      loading_rows: false,
      // Defer mounting the GmapMap until after first paint. Loading the
      // Google Maps SDK + rendering all reserved-territory polygons on
      // the synchronous mount path was the largest single contributor
      // to the page feeling "frozen for 20 seconds."
      // Off by default — the standalone summary GmapMap was the dominant
      // contributor to "page freezes 20s on first load." User opts in
      // with the Show map button.
      map_ready: false,
      show_map: false,
      rows: [],
      search: '',
      // Address exclusivity checker state.
      addr_open: false,
      addr_query: '',
      addr_zip: '',
      addr_point: null,
      addr_checking: false,
      addr_error: '',
      addr_result: null,
      addr_suggestions: [],
      addr_suggest_open: false,
      mode_filter: [],
      mode_filter_open: false,
      mode_filter_options: ['calculated', 'custom', 'free'],
      sort_key: 'signup_date',
      sort_dir: 'desc',
      columns: [
        { key: 'owner_display_name', label: 'Owner', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'address', label: 'Address', sortable: true },
        { key: 'city_state', label: 'City, ST', sortable: true },
        { key: 'market_population', label: 'Population', sortable: true, numeric: true },
        { key: 'radius_miles', label: 'Radius', sortable: true, numeric: true },
        { key: 'team_total', label: 'Users', sortable: true, numeric: true },
        { key: 'pricing_mode', label: 'Mode', sortable: true },
        { key: 'monthly_price_effective', label: 'Price', sortable: true, numeric: true },
        { key: 'next_paid_at', label: 'Next payment', sortable: true },
        { key: 'signup_date', label: 'Registered', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
      ],
      detail: null,
      detail_open: false,
      detail_loading: false,
      detail_error: '',
      selected_owner_id: null,
      edit_zips_open: false,
      // Inline-editable Address field state. Same tenant.address field
      // shown on the public account page — saving here writes to that
      // exact field so both views stay in sync.
      editing_address: false,
      address_draft: '',
      address_saving: false,
      address_error: '',
      override_price_open: false,
      add_market_open: false,
      forecast_open: false,
      // Set to { tenant_id, name, mode: 'create' | 'edit' } when actively
      // drawing. Triggers the fullscreen MarketSelection overlay.
      drawing_for: null,
      // Set once drawing is "Done" → triggers the confirm dialog before commit.
      grant_pending: null,
      grant_reason: '',
      grant_error: '',
      granting: false,
      // Pricing controls inside the confirm dialog. Default 'calculated'
      // matches the server default; super-user can flip to free or custom
      // before saving so they don't have to do a second-pass override.
      grant_pricing_mode: 'calculated',
      grant_custom_amount: 0,
      // Delete-confirm state.
      delete_pending: null,
      delete_reason: '',
      delete_error: '',
      deleting: false,
      map_center: { lat: 39.5, lng: -98.35 },
      map_zoom: 4,
      map_current_zoom: 4,
      map_options: {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'greedy',
      },
    };
  },
  computed: {
    ...mapGetters('Market_billing', ['all_taken_markets_by_owner']),
    google: gmapApi,
    reservedAsPins() {
      // Show a dot for each market whose boundary would be too small to be
      // useful at the current zoom. Per-market threshold lets a 1,000-mile
      // territory pop into outline at country zoom while a 50-mile one
      // stays a dot until the user zooms in.
      const out = {};
      for (const [k, m] of Object.entries(this.all_taken_markets_by_owner)) {
        if (this.map_current_zoom < (m.swap_zoom || 10)) out[k] = m;
      }
      return out;
    },
    reservedAsPolygons() {
      const out = {};
      for (const [k, m] of Object.entries(this.all_taken_markets_by_owner)) {
        if (this.map_current_zoom >= (m.swap_zoom || 10)) out[k] = m;
      }
      return out;
    },
    reservedPinIcon() {
      if (!this.google) return undefined;
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: '#B71C1C',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      };
    },
    grantValid() {
      if (!this.grant_pending) return false;
      if (this.grant_pricing_mode === 'custom') {
        return typeof this.grant_custom_amount === 'number' && this.grant_custom_amount >= 0;
      }
      return ['calculated', 'free'].includes(this.grant_pricing_mode);
    },
    filteredRows() {
      let rows = this.rows;
      if (this.mode_filter.length) {
        rows = rows.filter((r) => this.mode_filter.includes(r.pricing_mode));
      }
      if (!this.search) return rows;
      const q = this.search.toLowerCase();
      return rows.filter((r) => {
        const hay = [
          r.owner_display_name, r.email, r.address, r.city_state,
          r.pricing_mode, r.status, r.coupon && r.coupon.id,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    },

    totalMonthly() {
      return this.filteredRows.reduce((sum, r) => {
        const v = Number(r.monthly_price_effective);
        return sum + (isFinite(v) ? v : 0);
      }, 0);
    },
    totalPopulation() {
      return this.filteredRows.reduce((sum, r) => {
        const v = Number(r.market_population);
        return sum + (isFinite(v) ? v : 0);
      }, 0);
    },
    totalUsers() {
      return this.filteredRows.reduce((sum, r) => sum + this.teamTotal(r.team), 0);
    },

    sortedRows() {
      const rows = this.filteredRows.slice();
      const key = this.sort_key;
      const dir = this.sort_dir === 'asc' ? 1 : -1;
      const valueOf = (row) => {
        if (key === 'team_total') {
          const t = row.team || {};
          return (t.agents || 0) + (t.vendors || 0) + (t.employees || 0) + (t.homeowners || 0);
        }
        if (key === 'signup_date') return this._auditTs({ ts: row.signup_date });
        // Next Payment column displays next_paid_at and falls back to
        // next_renewal_at — sort the same way so the order matches what
        // the user sees.
        if (key === 'next_paid_at') return row.next_paid_at || row.next_renewal_at || '';
        const v = row[key];
        if (v == null) return '';
        return v;
      };
      rows.sort((a, b) => {
        const va = valueOf(a);
        const vb = valueOf(b);
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dir;
      });
      return rows;
    },

    /* ── Detail-drawer Market section enrichments ──────────────────────
       All derived from `detail.zip_details` (centroids + city/state) and
       `detail.audit` (timestamps + super-user identity). Backend already
       returns this data — these are pure client-side computeds. */

    detailZipPoints() {
      if (!this.detail || !Array.isArray(this.detail.zip_details)) return [];
      return this.detail.zip_details.filter(
        (z) => typeof z.lat === 'number' && typeof z.lng === 'number',
      );
    },

    marketCentroid() {
      const pts = this.detailZipPoints;
      if (!pts.length) return null;
      let sumLat = 0; let sumLng = 0;
      for (const p of pts) { sumLat += p.lat; sumLng += p.lng; }
      return { lat: sumLat / pts.length, lng: sumLng / pts.length };
    },

    marketCenter() {
      const c = this.marketCentroid;
      const pts = this.detailZipPoints;
      if (!c || !pts.length) return '';
      // ZIP whose centroid is closest to the polygon centroid — Haversine
      // would be more accurate but the squared-degree shortcut is fine
      // here (we're picking a winner from the same set, not measuring).
      let best = pts[0]; let bestD = Infinity;
      for (const p of pts) {
        const dLat = p.lat - c.lat;
        const dLng = p.lng - c.lng;
        const d = dLat * dLat + dLng * dLng;
        if (d < bestD) { bestD = d; best = p; }
      }
      const city = best.city || '';
      const state = best.state || '';
      if (city && state) return `${city}, ${state}`;
      return city || state || '';
    },

    marketStatesCovered() {
      const pts = this.detailZipPoints;
      if (!pts.length) return '';
      const counts = {};
      for (const p of pts) {
        const s = (p.state || '').toUpperCase();
        if (!s) continue;
        counts[s] = (counts[s] || 0) + 1;
      }
      const ordered = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      return ordered.map(([s, n]) => `${s} (${n})`).join(', ');
    },

    marketTopCities() {
      const pts = this.detailZipPoints;
      if (!pts.length) return '';
      const counts = {};
      for (const p of pts) {
        const city = p.city || '';
        const state = (p.state || '').toUpperCase();
        if (!city) continue;
        const key = state ? `${city}, ${state}` : city;
        counts[key] = (counts[key] || 0) + 1;
      }
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([k]) => k)
        .join(' · ');
    },

    marketSpanMiles() {
      const pts = this.detailZipPoints;
      if (pts.length < 2) return 0;
      // Bounding-box diagonal in miles. Latitude is ~69 mi/deg; longitude
      // shrinks toward the poles, so we scale by cos(meanLat).
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      for (const p of pts) {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
      }
      const meanLatRad = ((minLat + maxLat) / 2) * Math.PI / 180;
      const dLatMi = (maxLat - minLat) * 69;
      const dLngMi = (maxLng - minLng) * 69 * Math.cos(meanLatRad);
      return Math.round(Math.sqrt(dLatMi * dLatMi + dLngMi * dLngMi));
    },

    marketAuditEntry() {
      // Returns { granted, lastEdited } picked from the audit log.
      const audit = (this.detail && this.detail.audit) || [];
      let granted = null;
      let lastEdited = null;
      for (const a of audit) {
        if (a.action === 'grant_market' && (!granted || this._auditTs(a) > this._auditTs(granted))) {
          granted = a;
        }
        if ((a.action === 'zip_edit' || a.action === 'price_override' || a.action === 'grant_market')
            && (!lastEdited || this._auditTs(a) > this._auditTs(lastEdited))) {
          lastEdited = a;
        }
      }
      // Don't redundantly show "Last edited" when nothing has happened
      // since the grant.
      if (granted && lastEdited && this._auditTs(granted) === this._auditTs(lastEdited)) {
        lastEdited = null;
      }
      return { granted, lastEdited };
    },

    marketGrantedAt() {
      const e = this.marketAuditEntry.granted;
      return e ? e.ts : null;
    },
    marketGrantedBy() {
      const e = this.marketAuditEntry.granted;
      return e ? (e.super_user_email || e.super_user_phone || null) : null;
    },
    marketLastEditedAt() {
      const e = this.marketAuditEntry.lastEdited;
      return e ? e.ts : null;
    },
    marketLastEditedBy() {
      const e = this.marketAuditEntry.lastEdited;
      return e ? (e.super_user_email || e.super_user_phone || null) : null;
    },

    /**
     * Latest reason note attached to a pricing change. Looks for the
     * most recent price_override audit entry; falls back to the grant
     * itself if pricing has never been overridden after grant. Reasons
     * from zip-only edits aren't shown here — those would surface on a
     * "ZIPs note" line if we add one.
     */
    pricingNote() {
      const audit = (this.detail && this.detail.audit) || [];
      let chosen = null;
      for (const a of audit) {
        if (!a.reason) continue;
        if (a.action === 'price_override') {
          if (!chosen || this._auditTs(a) > this._auditTs(chosen)) chosen = a;
        }
      }
      if (!chosen) {
        // No price override note — fall back to the grant's reason if
        // present (some super-users put pricing context there).
        for (const a of audit) {
          if (!a.reason || a.action !== 'grant_market') continue;
          if (!chosen || this._auditTs(a) > this._auditTs(chosen)) chosen = a;
        }
      }
      if (!chosen) return null;
      return {
        text: chosen.reason,
        by: chosen.super_user_email || chosen.super_user_phone || null,
        ts: chosen.ts,
      };
    },
  },
  methods: {
    ...mapActions('Market_billing', ['fetch_markets', 'load_zip_data', 'subscribe_markets']),

    toggleModeFilter() {
      this.mode_filter_open = !this.mode_filter_open;
    },

    async onChangeBillingDate() {
      if (!this.detail) return;
      const current = this.detail.next_renewal_at
        ? new Date(this.detail.next_renewal_at).toISOString().slice(0, 10)
        : '';
      // eslint-disable-next-line no-alert
      const input = window.prompt(
        'New next-payment date (YYYY-MM-DD). Sets Stripe trial_end so the next invoice fires that day.',
        current,
      );
      if (!input) return;
      const ms = Date.parse(`${input}T12:00:00Z`);
      if (!ms || isNaN(ms)) {
        // eslint-disable-next-line no-alert
        window.alert('Invalid date — expected YYYY-MM-DD.');
        return;
      }
      // eslint-disable-next-line no-alert
      const reason = window.prompt('Reason (logged in audit; optional):') || null;
      try {
        await setMarketBillingDate(this.detail.tenant_id, ms, reason);
        // Refetch the drawer so the new date renders.
        const refreshed = await getMarketDetail(this.detail.tenant_id);
        this.detail = refreshed;
      } catch (err) {
        // eslint-disable-next-line no-alert
        window.alert(`Couldn't change billing date: ${humanizeAxiosError(err)}`);
      }
    },

    async onChangeCoupon() {
      if (!this.detail) return;
      const current = (this.detail.coupon && this.detail.coupon.id) || '';
      // eslint-disable-next-line no-alert
      const input = window.prompt(
        'Coupon ID to apply (case-sensitive — must exist on the Stripe account):',
        current,
      );
      if (!input) return;
      // eslint-disable-next-line no-alert
      const reason = window.prompt('Reason (logged in audit; optional):') || null;
      try {
        await setMarketCoupon(this.detail.tenant_id, { coupon_id: input.trim(), reason });
        const refreshed = await getMarketDetail(this.detail.tenant_id);
        this.detail = refreshed;
      } catch (err) {
        // eslint-disable-next-line no-alert
        window.alert(`Couldn't apply coupon: ${humanizeAxiosError(err)}`);
      }
    },

    async onRemoveCoupon() {
      if (!this.detail) return;
      // eslint-disable-next-line no-alert
      const ok = window.confirm('Remove the coupon from this subscription? Next invoice will charge the full price.');
      if (!ok) return;
      // eslint-disable-next-line no-alert
      const reason = window.prompt('Reason (logged in audit; optional):') || null;
      try {
        await setMarketCoupon(this.detail.tenant_id, { remove: true, reason });
        const refreshed = await getMarketDetail(this.detail.tenant_id);
        this.detail = refreshed;
      } catch (err) {
        // eslint-disable-next-line no-alert
        window.alert(`Couldn't remove coupon: ${humanizeAxiosError(err)}`);
      }
    },

    // Status column source-of-truth blends Stripe's billing_status
    // ("active", "trialing", "past_due", …) with manually-set tenant.status
    // ("Active", "Free", …). Stripe sends lowercase, ops sets titlecase —
    // so the column reads "active" next to "Active". Title-case at display
    // time, leaving the raw value alone in the API for sorting/search.
    statusLabel(s) {
      if (!s) return '—';
      const str = String(s).trim();
      if (!str) return '—';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },

    // The Next Payment column should answer "when does Kiey next get money
    // from this account?" — not "when does the next invoice fire?". For a
    // KIEY_60D_FREE-style 100%-off repeating coupon, the next few invoices
    // are $0, so we surface next_paid_at (computed server-side) instead.
    // Falls back to next_renewal_at on older payloads + when there's no
    // coupon at all.
    nextPaymentLabel(row) {
      const c = row && row.coupon;
      if (c && c.percent_off === 100 && c.duration === 'forever') {
        return '— (free tier)';
      }
      const ts = (row && row.next_paid_at) || (row && row.next_renewal_at) || null;
      return this.formatDate(ts);
    },

    couponTooltip(c) {
      if (!c) return '';
      if (typeof c.percent_off === 'number' && c.percent_off > 0) {
        const pct = c.percent_off % 1 === 0 ? c.percent_off : c.percent_off.toFixed(1);
        if (c.duration === 'forever') return `${pct}% off forever`;
        if (c.duration === 'repeating' && c.duration_in_months) {
          return `${pct}% off for ${c.duration_in_months} mo`;
        }
        return `${pct}% off`;
      }
      if (typeof c.amount_off === 'number' && c.amount_off > 0) {
        return `$${(c.amount_off / 100).toFixed(2)} off`;
      }
      return c.id;
    },

    downloadCsv() {
      const headers = [
        'Owner', 'Email', 'Phone', 'Address', 'City, ST', 'ZIPs', 'Population',
        'Radius (mi)', 'Mode', 'Coupon', 'Price/mo', 'Next payment', 'Registered', 'Status',
      ];
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      };
      const fmt = (ts) => {
        if (!ts) return '';
        const ms = typeof ts === 'number'
          ? (ts > 1e12 ? ts : ts * 1000)
          : (ts && ts.toMillis ? ts.toMillis() : (ts && ts._seconds ? ts._seconds * 1000 : Date.parse(ts)));
        if (!ms || isNaN(ms)) return '';
        return new Date(ms).toISOString().slice(0, 10);
      };
      const lines = [headers.map(esc).join(',')];
      for (const r of this.sortedRows) {
        lines.push([
          r.owner_display_name || '',
          r.email || '',
          r.phone || '',
          r.address || '',
          r.city_state || '',
          r.zip_count || 0,
          r.market_population || 0,
          r.radius_miles || 0,
          this.pricingModeLabel(r.pricing_mode) || '',
          (r.coupon && r.coupon.id) || '',
          r.monthly_price_effective || 0,
          fmt(r.next_paid_at || r.next_renewal_at),
          fmt(r.signup_date),
          this.statusLabel(r.status),
        ].map(esc).join(','));
      }
      // Append a total row so the CSV mirrors the on-screen footer.
      lines.push([
        'Total monthly', '', '', '', '', '', '', '', '', '',
        this.totalMonthly, '', '', '',
      ].map(esc).join(','));
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `markets-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    async refresh({ force = false } = {}) {
      // Map data first — same path kiey.com/my-market uses, fast.
      // `force: true` busts the 60s fetch_markets cache so the map reflects
      // a grant/edit/release that just succeeded without a manual reload.
      try {
        await Promise.all([this.load_zip_data(), this.fetch_markets({ force })]);
      } catch (e) {
        console.error('[markets] failed to load map data', e);
      }
      // Table loads in the background. Don't block the map on it: the
      // admin endpoint does N+1 reads (now parallelized server-side, but
      // still slower than the public map fetch). Page is interactive
      // immediately; rows fill in when ready.
      this.loading_rows = true;
      listMarkets()
        .then((rows) => { this.rows = rows; })
        .catch((e) => { console.error('[markets] list endpoint failed', e); })
        .finally(() => { this.loading_rows = false; });
    },

    async openDetail(row) {
      this.detail_open = true;
      this.selected_owner_id = row.tenant_id;
      this.detail = null;
      this.detail_error = '';
      this.detail_loading = true;
      try {
        this.detail = await getMarketDetail(row.tenant_id);
      } catch (e) {
        console.error('[markets] detail failed', e);
        const status = e.response && e.response.status;
        if (status === 404) {
          this.detail_error = 'Tenant not found in the tenants collection.';
        } else {
          this.detail_error = (e.response && e.response.data && e.response.data.errors && e.response.data.errors.join(', '))
            || e.message || 'Failed to load tenant detail.';
        }
      } finally {
        this.detail_loading = false;
      }
    },
    closeDetail() {
      this.detail_open = false;
      this.detail_error = '';
    },

    polygonOptionsFor(ownerId, m) {
      const isFree = m.pricing_mode === 'free';
      const isSelected = ownerId === this.selected_owner_id;
      return {
        strokeColor: isFree ? FREE_BORDER : PAID_BORDER,
        strokeOpacity: 0.9,
        strokeWeight: isSelected ? 3 : 2,
        fillColor: isSelected ? SELECTED_FILL : (isFree ? FREE_FILL : PAID_FILL),
        fillOpacity: 0.45,
        clickable: true,
        zIndex: isSelected ? 10 : 5,
      };
    },
    onPolygonClick(ownerId) {
      this.selected_owner_id = ownerId;
      const row = this.rows.find((r) => r.tenant_id === ownerId);
      if (row) this.openDetail(row);
    },
    hullCentroid(hull) {
      if (!hull || !hull.length) return { lat: 0, lng: 0 };
      let lat = 0, lng = 0;
      for (const p of hull) { lat += p.lat; lng += p.lng; }
      return { lat: lat / hull.length, lng: lng / hull.length };
    },

    pricingModeLabel(mode) {
      if (mode === 'free') return 'Free';
      if (mode === 'custom') return 'Custom';
      return 'Calculated';
    },

    teamTotal(team) {
      const t = team || {};
      // Prefer the backend-supplied total when present (matches what the
      // detail drawer shows), fall back to summing the breakdown.
      if (typeof t.total === 'number') return t.total;
      return (t.agents || 0) + (t.vendors || 0) + (t.employees || 0) + (t.homeowners || 0);
    },

    startEditAddress() {
      if (!this.detail) return;
      this.address_draft = this.detail.address || '';
      this.address_error = '';
      this.editing_address = true;
      // Wire Google Places Autocomplete on the input as soon as it
      // mounts. $gmapApiPromiseLazy lazily kicks off the SDK load if
      // it hasn't fired yet (e.g. user opened the drawer without ever
      // showing the map), so the autocomplete works without depending
      // on the summary map being mounted.
      this.$nextTick(() => {
        if (typeof this.$gmapApiPromiseLazy === 'function') {
          this.$gmapApiPromiseLazy().then(() => this.attachAddressAutocomplete());
        } else {
          this.attachAddressAutocomplete();
        }
      });
    },
    attachAddressAutocomplete() {
      const input = this.$refs.addressInput;
      if (!input || !window.google || !window.google.maps || !window.google.maps.places) return;
      // Tear down any previous instance — switching tenants in the
      // drawer mounts a fresh input each time.
      if (this._addressPlaceListener) {
        try { window.google.maps.event.removeListener(this._addressPlaceListener); } catch (e) { /* noop */ }
        this._addressPlaceListener = null;
      }
      this._addressAutocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: ['us', 'ca'] },
      });
      this._addressPlaceListener = this._addressAutocomplete.addListener('place_changed', () => {
        const place = this._addressAutocomplete.getPlace();
        if (place && place.formatted_address) {
          this.address_draft = place.formatted_address;
        }
      });
    },
    detachAddressAutocomplete() {
      if (this._addressPlaceListener && window.google && window.google.maps) {
        try { window.google.maps.event.removeListener(this._addressPlaceListener); } catch (e) { /* noop */ }
      }
      this._addressPlaceListener = null;
      this._addressAutocomplete = null;
    },

    // ── Address exclusivity checker ────────────────────────────────────
    openAddressCheck() {
      this.addr_open = true;
      if (typeof this.$gmapApiPromiseLazy === 'function') {
        // Warm the Maps SDK so AutocompleteService/PlacesService are ready.
        this.$gmapApiPromiseLazy().catch(() => { /* noop */ });
      }
      this._addrEscHandler = (e) => { if (e.key === 'Escape') this.closeAddressCheck(); };
      document.addEventListener('keydown', this._addrEscHandler);
      this.$nextTick(() => {
        const input = this.$refs.addrCheckInput;
        if (input) input.focus();
      });
    },
    closeAddressCheck() {
      this.addr_open = false;
      this.addr_suggest_open = false;
      this.addr_suggestions = [];
      if (this._addrEscHandler) {
        document.removeEventListener('keydown', this._addrEscHandler);
        this._addrEscHandler = null;
      }
    },
    // Fetch predictions as the user types and render them in OUR OWN list
    // (see template `.addr-suggest`) — no Google `.pac-container`, so nothing
    // can flip above the input. Debounced.
    onAddrInput() {
      // Any edit invalidates a previously resolved selection.
      this.addr_zip = '';
      this.addr_point = null;
      this._addrResolvedQuery = '';
      const q = (this.addr_query || '').trim();
      if (this._addrSuggestTimer) clearTimeout(this._addrSuggestTimer);
      if (q.length < 3) { this.addr_suggestions = []; return; }
      this._addrSuggestTimer = setTimeout(() => this.fetchSuggestions(q), 180);
    },
    fetchSuggestions(q) {
      const places = window.google && window.google.maps && window.google.maps.places;
      if (!places || !places.AutocompleteService) { this.addr_suggestions = []; return; }
      if (!this._addrAcService) this._addrAcService = new places.AutocompleteService();
      this._addrAcService.getPlacePredictions(
        { input: q, types: ['address'], componentRestrictions: { country: ['us', 'ca'] } },
        (preds, status) => {
          if (status !== places.PlacesServiceStatus.OK || !preds || !preds.length) {
            this.addr_suggestions = [];
            return;
          }
          this.addr_suggestions = preds.slice(0, 5).map((p) => {
            const sf = p.structured_formatting || {};
            return {
              place_id: p.place_id,
              main: sf.main_text || p.description,
              secondary: sf.secondary_text || '',
              description: p.description,
            };
          });
          this.addr_suggest_open = true;
        },
      );
    },
    selectSuggestion(s) {
      if (!s) return;
      this.addr_query = s.description;
      this.addr_suggestions = [];
      this.addr_suggest_open = false;
      const places = window.google && window.google.maps && window.google.maps.places;
      if (!places || !places.PlacesService) { this.runAddressCheck(); return; }
      if (!this._addrPlacesService) {
        this._addrPlacesService = new places.PlacesService(document.createElement('div'));
      }
      this._addrPlacesService.getDetails(
        { placeId: s.place_id, fields: ['address_components', 'geometry', 'formatted_address'] },
        (place, status) => {
          if (status === places.PlacesServiceStatus.OK && place) {
            const ex = this.extractPlace(place);
            this.addr_zip = ex.zip;
            this.addr_point = ex.point;
            if (place.formatted_address) this.addr_query = place.formatted_address;
            this._addrResolvedQuery = (this.addr_query || '').trim();
          }
          this.runAddressCheck();
        },
      );
    },
    onAddrBlur() {
      // Delay so a suggestion mousedown resolves before the list hides.
      setTimeout(() => { this.addr_suggest_open = false; }, 150);
    },
    // Pull { zip, point } out of a Places result (autocomplete or details).
    extractPlace(place) {
      let zip = '';
      const comps = (place && place.address_components) || [];
      for (const c of comps) {
        if (c.types && c.types.indexOf('postal_code') !== -1) {
          zip = c.long_name || c.short_name || '';
          break;
        }
      }
      let point = null;
      if (place && place.geometry && place.geometry.location) {
        const loc = place.geometry.location;
        const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
        const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
        if (typeof lat === 'number' && typeof lng === 'number') point = { lat, lng };
      }
      return { zip: zip ? String(zip).trim() : '', point };
    },
    // Resolve a free-text address (typed or pasted) via the Places API, so the
    // checker never depends on the suggestion dropdown. findPlaceFromQuery
    // handles complete address strings that as-you-type autocomplete won't
    // predict; getDetails then yields postal_code + geometry. Uses only the
    // Places library (already loaded), not the separate Geocoding API.
    resolveQueryViaPlaces(query) {
      return new Promise((resolve) => {
        const places = window.google && window.google.maps && window.google.maps.places;
        if (!places || !places.PlacesService) { resolve(null); return; }
        const svc = new places.PlacesService(document.createElement('div'));
        svc.findPlaceFromQuery({ query, fields: ['place_id'] }, (cands, status) => {
          if (status !== places.PlacesServiceStatus.OK || !cands || !cands.length) {
            resolve(null);
            return;
          }
          svc.getDetails(
            { placeId: cands[0].place_id, fields: ['address_components', 'geometry', 'formatted_address'] },
            (place, st2) => {
              resolve(st2 === places.PlacesServiceStatus.OK && place ? place : null);
            },
          );
        });
      });
    },
    async runAddressCheck() {
      if (this.addr_checking) return; // guard against double-submit (Enter selects + keyup)
      const query = (this.addr_query || '').trim();
      if (!query) { this.addr_error = 'Enter an address first.'; return; }
      this.addr_suggestions = [];
      this.addr_suggest_open = false;
      this.addr_checking = true;
      this.addr_error = '';
      this.addr_result = null;
      try {
        let zip = this.addr_zip;
        let point = this.addr_point;
        // If the current text wasn't already resolved (e.g. typed/pasted in full
        // without picking a suggestion), resolve it via Places now.
        if (!point || this._addrResolvedQuery !== query) {
          const place = await this.resolveQueryViaPlaces(query);
          if (!place) {
            this.addr_error = 'Could not find that address. Try including the city, state, and ZIP.';
            return;
          }
          const ex = this.extractPlace(place);
          zip = ex.zip;
          point = ex.point;
          this.addr_zip = zip;
          this.addr_point = point;
          this._addrResolvedQuery = query;
          if (place.formatted_address) this.addr_query = place.formatted_address;
        }
        const payload = { zip: zip || '' };
        if (point) { payload.lat = point.lat; payload.lng = point.lng; }
        this.addr_result = await checkAddress(payload);
      } catch (e) {
        this.addr_error = humanizeAxiosError(e);
      } finally {
        this.addr_checking = false;
      }
    },
    clearAddressCheck() {
      this.addr_query = '';
      this.addr_zip = '';
      this.addr_point = null;
      this.addr_result = null;
      this.addr_error = '';
      this.addr_suggestions = [];
      this.addr_suggest_open = false;
      this._addrResolvedQuery = '';
    },
    detachAddressCheckAutocomplete() {
      // Custom dropdown has no Google widget to tear down — just clear the
      // pending suggestions debounce timer.
      if (this._addrSuggestTimer) {
        clearTimeout(this._addrSuggestTimer);
        this._addrSuggestTimer = null;
      }
    },
    cancelEditAddress() {
      this.detachAddressAutocomplete();
      this.editing_address = false;
      this.address_error = '';
      this.address_draft = '';
    },
    async saveAddress() {
      if (!this.detail || this.address_saving) return;
      this.address_saving = true;
      this.address_error = '';
      const tenantId = this.detail.tenant_id;
      const next = (this.address_draft || '').trim();
      try {
        await updateTenantAddress(tenantId, next, null);
        // Optimistic local update so the drawer reflects the new value
        // immediately. The next refresh will reconcile from the server.
        this.detail = { ...this.detail, address: next };
        const idx = this.rows.findIndex((r) => r.tenant_id === tenantId);
        if (idx >= 0) this.$set(this.rows, idx, { ...this.rows[idx], address: next });
        this.detachAddressAutocomplete();
        this.editing_address = false;
        this.address_draft = '';
      } catch (e) {
        this.address_error = humanizeAxiosError(e);
      } finally {
        this.address_saving = false;
      }
    },

    toggleSummaryMap() {
      this.show_map = !this.show_map;
      if (this.show_map && !this.map_ready) {
        // Defer the heavy Google Maps SDK + polygon render off the click
        // tick so the button feels responsive.
        this.$nextTick(() => setTimeout(() => {
          this.map_ready = true;
          this.$nextTick(() => {
            if (this.$refs.mapRef && this.$refs.mapRef.$mapPromise) {
              this.$refs.mapRef.$mapPromise.then((mapInstance) => {
                this.map_current_zoom = mapInstance.getZoom() || this.map_zoom;
                mapInstance.addListener('zoom_changed', () => {
                  this.map_current_zoom = mapInstance.getZoom();
                });
              });
            }
          });
        }, 0));
      }
    },

    modeBadgeClass(mode) {
      if (mode === 'free') return 'badge-free';
      if (mode === 'custom') return 'badge-custom';
      return 'badge-calculated';
    },

    toggleSort(key) {
      if (this.sort_key === key) {
        this.sort_dir = this.sort_dir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sort_key = key;
        // Default direction: numeric/date columns descend (biggest/newest
        // first), text columns ascend.
        const col = this.columns.find((c) => c.key === key);
        const numericLike = col && (col.numeric || key === 'signup_date');
        this.sort_dir = numericLike ? 'desc' : 'asc';
      }
    },

    formatNumber(n) {
      if (typeof n !== 'number') return '—';
      return n.toLocaleString('en-US');
    },

    formatDate(value) {
      if (!value) return '—';
      let d;
      if (value && typeof value === 'object' && value._seconds) {
        d = new Date(value._seconds * 1000);
      } else if (value && typeof value === 'object' && value.seconds) {
        d = new Date(value.seconds * 1000);
      } else {
        d = new Date(value);
      }
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    _auditTs(entry) {
      const t = entry && entry.ts;
      if (!t) return 0;
      if (typeof t === 'object' && t._seconds) return t._seconds * 1000;
      if (typeof t === 'object' && t.seconds) return t.seconds * 1000;
      const ms = new Date(t).getTime();
      return Number.isNaN(ms) ? 0 : ms;
    },

    async onPriceSaved() {
      this.override_price_open = false;
      await this.refresh({ force: true });
      if (this.selected_owner_id) {
        this.detail = await getMarketDetail(this.selected_owner_id);
      }
    },
    /* ── Add / Edit market drawing flow ──────────────────────────── */
    onTenantSelected(tenant) {
      this.add_market_open = false;
      this.startDrawingForTenant({
        tenant_id: tenant.tenant_id,
        name: tenant.name || '',
        mode: 'create',
      });
    },
    startEditingFromDrawer() {
      if (!this.detail) return;
      const name = this.detail.tenant_name || (this.detail.owner && this.detail.owner.name) || this.detail.tenant_id;
      this.detail_open = false;
      this.startDrawingForTenant({
        tenant_id: this.detail.tenant_id,
        name,
        mode: 'edit',
      });
    },
    askDeleteFromDrawer() {
      if (!this.detail) return;
      const name = this.detail.tenant_name || (this.detail.owner && this.detail.owner.name) || this.detail.tenant_id;
      this.delete_pending = {
        tenant_id: this.detail.tenant_id,
        name,
        zip_count: this.detail.zips ? this.detail.zips.length : 0,
      };
      this.delete_reason = '';
      this.delete_error = '';
    },
    cancelDelete() {
      this.delete_pending = null;
      this.delete_error = '';
    },
    async confirmDelete() {
      if (!this.delete_pending) return;
      this.deleting = true;
      this.delete_error = '';
      try {
        await releaseMarket(this.delete_pending.tenant_id, this.delete_reason || null);
        this.delete_pending = null;
        this.detail_open = false;
        this.detail = null;
        this.selected_owner_id = null;
        await this.refresh({ force: true });
      } catch (e) {
        this.delete_error = humanizeAxiosError(e);
      } finally {
        this.deleting = false;
      }
    },
    startDrawingForTenant({ tenant_id, name, mode }) {
      // Edit mode: stash the existing ZIPs so MarketSelection can fit the
      // map to the right area on mount. MarketSelection's hydrateAdminAreas
      // then rebuilds the tenant's existing areas as committed areas (one
      // per area_index) the super-user can keep, delete, or add to.
      let initial_zips = [];
      if (mode === 'edit' && this.detail && Array.isArray(this.detail.zips)) {
        initial_zips = this.detail.zips;
      }
      this.drawing_for = { tenant_id, name, mode, initial_zips };
      this.grant_error = '';
      this.grant_reason = '';
      this.grant_pricing_mode = (mode === 'edit' && this.detail && this.detail.pricing_mode)
        ? this.detail.pricing_mode : 'calculated';
      this.grant_custom_amount = (mode === 'edit' && this.detail && typeof this.detail.monthly_price_override === 'number')
        ? this.detail.monthly_price_override : 0;
      this.$store.commit('Market_billing/SET_SELECTED_ZIPS', []);
    },
    onDrawingDone({ zips, areas, population, calculated }) {
      if (!this.drawing_for || !zips || !zips.length) return;
      this.grant_pending = {
        tenant_id: this.drawing_for.tenant_id,
        name: this.drawing_for.name,
        mode: this.drawing_for.mode,
        zips: [...zips],
        areas: Array.isArray(areas) ? areas.map((a) => [...a]) : null,
        population: population || 0,
        calculated: calculated || 0,
      };
    },
    exitDrawing() {
      this.drawing_for = null;
      this.grant_pending = null;
      this.grant_error = '';
      this.grant_reason = '';
      this.$store.commit('Market_billing/SET_SELECTED_ZIPS', []);
    },
    cancelGrant() {
      // Back to drawing — keep the polygon, dismiss the confirm dialog.
      this.grant_pending = null;
      this.grant_error = '';
    },
    async confirmGrant() {
      if (!this.grant_pending || !this.grantValid) return;
      this.granting = true;
      this.grant_error = '';
      try {
        const tenantId = this.grant_pending.tenant_id;
        const isEdit = this.grant_pending.mode === 'edit';
        if (isEdit) {
          await setMarketZips(
            tenantId,
            this.grant_pending.zips,
            this.grant_reason || null,
            this.grant_pending.areas,
          );
        } else {
          await grantMarket(
            tenantId,
            this.grant_pending.zips,
            this.grant_reason || null,
            this.grant_pending.areas,
          );
        }
        // Apply pricing override only when it deviates from the default
        // (calculated). For 'free' / 'custom' we push the override; for
        // 'calculated' we still send it on edit if the prior tenant had
        // an override that should be cleared.
        if (this.grant_pricing_mode !== 'calculated' || isEdit) {
          const pricePayload = {
            pricing_mode: this.grant_pricing_mode,
            reason: this.grant_reason || null,
          };
          if (this.grant_pricing_mode === 'custom') {
            pricePayload.override = Number(this.grant_custom_amount);
          }
          await setMarketPrice(tenantId, pricePayload);
        }

        this.exitDrawing();
        await this.refresh({ force: true });
        const row = this.rows.find((r) => r.tenant_id === tenantId);
        if (row) await this.openDetail(row);
      } catch (e) {
        this.grant_error = humanizeAxiosError(e);
      } finally {
        this.granting = false;
      }
    },
  },
  beforeDestroy() {
    this.detachAddressAutocomplete();
    this.detachAddressCheckAutocomplete();
    if (this._addrEscHandler) {
      document.removeEventListener('keydown', this._addrEscHandler);
      this._addrEscHandler = null;
    }
  },
  mounted() {
    // Paint the shell first, then start the heavy work. Without this defer,
    // `refresh()` (axios + 2.4MB ZIP JSON parse + Vuex commits) and the
    // synchronous Google Maps SDK boot all run inside the same mount tick
    // and the user sees a frozen page for many seconds.
    this.$nextTick(() => {
      this.refresh();
      // Defer the live listener subscription. The initial Firestore
      // snapshot can pull every market doc, which on a populated DB
      // costs hundreds of ms — never on the critical paint path.
      setTimeout(() => this.subscribe_markets(), 3000);
    });
  },
};
</script>

<style scoped>
.super-admin-markets {
  padding: 16px 24px 48px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Lato', sans-serif;
  color: #1f2937;
}

.page-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; margin-bottom: 16px;
}
.page-header h1 { font-size: 22px; font-weight: 700; color: #061D47; margin: 0; }
.header-actions { display: flex; align-items: center; gap: 12px; }
.search-input {
  width: 320px; padding: 4px 10px;
  border: 1px solid #c2cbe0; border-radius: 5px; font-size: 12px;
  background: #fff; height: 28px; box-sizing: border-box;
}
.search-input:focus { outline: none; border-color: #061D47; box-shadow: 0 0 0 2px rgba(6,29,71,0.12); }

/* ── Address exclusivity checker ───────────────────────────────────── */
.addr-check { margin: 0 0 14px; }
.addr-check-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fafbfd;
  border: 1px solid #d6dbe5;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #061D47;
  text-align: left;
}
.addr-check-toggle:hover { background: #f1f4f9; }
.addr-check-pin { font-size: 14px; }
.addr-check-hint { font-weight: 400; margin-left: 4px; }

/* Centered modal popup */
.addr-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 29, 71, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11000;
  padding: 20px;
}
.addr-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 560px;
  padding: 22px 24px 24px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.3);
}
.addr-modal-head { display: flex; align-items: center; justify-content: space-between; }
.addr-modal-title { margin: 0; font-size: 19px; font-weight: 700; color: #061D47; }
.addr-modal-close {
  background: transparent; border: none; cursor: pointer;
  font-size: 26px; line-height: 1; color: #6b7280; padding: 0 4px;
}
.addr-modal-close:hover { color: #061D47; }
.addr-modal-sub { margin: 6px 0 16px; font-size: 13px; color: #6b7280; }
.addr-check-row { display: flex; gap: 8px; align-items: flex-start; flex-wrap: wrap; }
.addr-check-input-wrap { position: relative; flex: 1; min-width: 240px; max-width: 460px; }
.addr-check-input { width: 100%; }
/* Our own suggestions list — normal-flow absolute below the input, inside the
   panel. Cannot flip above (unlike Google's body-appended .pac-container). */
.addr-suggest {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #d6dbe5;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(6, 29, 71, 0.14);
  max-height: 260px;
  overflow-y: auto;
  z-index: 20;
}
.addr-suggest-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13.5px;
  line-height: 1.3;
}
.addr-suggest-item:hover { background: #f1f4f9; }
.addr-suggest-main { color: #061D47; font-weight: 600; }
.addr-suggest-sec { color: #6b7280; font-size: 12px; }
.addr-suggest-credit {
  padding: 5px 12px 3px;
  text-align: right;
  font-size: 10px;
  color: #9aa3b2;
  border-top: 1px solid #eef1f6;
}
.addr-check-btn {
  background: #061D47;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.addr-check-btn:disabled { opacity: 0.5; cursor: default; }
.addr-check-clear {
  background: transparent;
  border: 1px solid #d6dbe5;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}
.addr-check-error { color: #b42318; font-size: 13px; margin: 10px 0 0; }
.addr-check-result { margin-top: 12px; }
.addr-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}
.addr-badge-yes { background: #e7f6ec; color: #1a7f37; }
.addr-badge-no { background: #fdeceb; color: #b42318; }
.addr-detail { margin-top: 8px; font-size: 14px; color: #1f2937; }
.addr-check-warn { margin: 8px 0 0; font-size: 12.5px; color: #92600a; background: #fff7e6; border-radius: 6px; padding: 6px 10px; }
.addr-check-foot { margin: 6px 0 0; }

.btn {
  border: 0; border-radius: 6px;
  padding: 8px 14px; font-size: 13px; font-weight: 600;
  cursor: pointer;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn.primary { background: #061D47; color: #fff; }
.btn.primary:hover:not(:disabled) { background: #0b2a66; }
.btn.outline {
  background: #fff; color: #061D47; border: 1px solid #061D47;
}
.btn.outline:hover { background: #eef1f5; }
.btn.danger {
  background: #fff; color: #b71c1c; border: 1px solid #b71c1c;
  margin-left: 8px;
}
.btn.danger:hover:not(:disabled) { background: #ffeded; }
.mt-1 { margin-top: 8px; }

.map-wrap {
  position: relative; height: 360px;
  border-radius: 10px; overflow: hidden; margin-bottom: 16px;
  background: #eef1f5;
}
/* While drawing/editing inline, give the MarketSelection room to fit
   its full toolbar + map + readout. The summary-map height (360px) is
   tight for the drawing UI and would force unwanted scrolling. */
.map-wrap.is-drawing {
  height: auto; min-height: 600px; overflow: visible;
}
.map { width: 100%; height: 100%; }
.map-placeholder {
  display: flex; align-items: center; justify-content: center;
  color: #6b7280; font-size: 13px;
  background: #eef1f5;
}
.map-legend {
  position: absolute; bottom: 10px; left: 10px;
  background: rgba(255,255,255,0.95);
  border-radius: 6px; padding: 8px 12px;
  font-size: 12px; color: #4b5563;
  display: flex; gap: 14px;
}
.legend-item { display: inline-flex; align-items: center; gap: 6px; }
.swatch { display: inline-block; width: 12px; height: 12px; border-radius: 3px; border: 1px solid rgba(0,0,0,0.2); }
.sw-paid { background: #FBE4E4; border-color: #7E0F0F; }
.sw-free { background: #D2F0EC; border-color: #0B5D55; }
.sw-selected { background: #FFE9B8; border-color: #C7A03A; }


.table-wrap {
  background: #fff; border-radius: 10px;
  border: 1px solid #eef1f5;
  overflow: hidden;
}
.table-toolbar {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 8px;
}
.table-toolbar .search-input { flex: 1; max-width: 320px; }
.csv-download-btn {
  margin-left: auto;
  background: #061D47;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.csv-download-btn:hover { background: #0a2e6f; }
.mode-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: 100%;
}
.badge-coupon {
  background: #fff7e0;
  color: #92580b;
  border: 1px solid #f0c97d;
  white-space: nowrap;
}
.markets-total-row td {
  border-top: 2px solid #061D47;
  background: #f1f5fb;
  font-weight: 600;
  padding: 12px 8px;
}
.markets-total-row td.num { text-align: right; }
.markets-total-label { text-align: left; color: #061D47; }
.th-filter-trigger {
  display: inline-block;
  margin-left: 6px;
  padding: 0 4px;
  font-size: 10px;
  color: #6a7388;
  cursor: pointer;
  border-radius: 3px;
}
.th-filter-trigger:hover { background: #e3eaf5; color: #061D47; }
.th-filter-trigger.active { background: #061D47; color: #fff; }
.markets-table th { position: relative; }
.th-filter-popover {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 5;
  background: #fff;
  border: 1px solid #e3eaf5;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(6,29,71,0.10);
  padding: 6px;
  min-width: 180px;
  font-family: Roboto, Arial, sans-serif;
  text-transform: none;
  font-weight: 400;
  letter-spacing: 0;
}
.th-filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 400;
  text-transform: capitalize;
  letter-spacing: 0;
  color: #041332;
  cursor: pointer;
  border-radius: 6px;
  user-select: none;
}
.th-filter-option input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid #c4ccdc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
}
.th-filter-option input[type="checkbox"]:checked {
  background: #2bace2;
  border-color: #2bace2;
}
.th-filter-option input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.th-filter-option:hover { background: #f5f8fc; }
.th-filter-actions {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eef1f6;
  margin-top: 4px;
  padding: 6px 4px 2px;
}
.link-btn {
  background: none;
  border: none;
  color: #2bace2;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
}
.link-btn:hover { text-decoration: underline; }
.ml-2 { margin-left: 8px; }
.markets-table {
  width: 100%; border-collapse: collapse; font-size: 11px;
}
.markets-table thead th {
  text-align: left; padding: 6px 8px;
  font-size: 9px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; color: #6b7280;
  border-bottom: 1px solid #eef1f5;
  background: #fafbfd;
  user-select: none;
}
.markets-table thead th.num { text-align: right; }
.markets-table thead th.sortable { cursor: pointer; }
.markets-table thead th.sortable:hover { color: #061D47; background: #f1f5fb; }
.markets-table thead th.sorted { color: #061D47; }
.markets-table thead th .sort-arrow {
  margin-left: 4px; font-size: 9px; color: #061D47;
}
.markets-table tbody td {
  padding: 6px 8px;
  border-bottom: 1px solid #f4f6f9;
  vertical-align: middle;
}
.markets-table tbody td.num { text-align: right; }
.markets-table tbody tr { cursor: pointer; }
.markets-table tbody tr:hover { background: #f7f9fc; }
.markets-table tbody tr.selected { background: #eaf2ff; }
.pad { padding: 16px 20px; }

.badge {
  display: inline-block;
  padding: 2px 6px; border-radius: 10px;
  font-size: 9px; font-weight: 700;
  letter-spacing: 0.04em; text-transform: uppercase;
}
.badge-free { background: #0B5D55; color: #fff; }
.badge-custom { background: #061D47; color: #fff; }
.badge-calculated { background: #eef1f5; color: #4b5563; }

.kv.kv-editable { align-items: flex-start; }
.kv-value-row, .kv-edit-row {
  display: inline-flex; align-items: center; gap: 8px;
  flex-wrap: wrap;
}
.inline-input {
  flex: 1; min-width: 220px;
  padding: 4px 8px; border: 1px solid #c2cbe0; border-radius: 4px;
  font-size: 13px; background: #fff;
}
.inline-input:focus { outline: none; border-color: #061D47; box-shadow: 0 0 0 2px rgba(6,29,71,0.12); }
.link-btn {
  background: transparent; border: 0; padding: 2px 4px;
  color: #061D47; font-size: 12px; font-weight: 600;
  cursor: pointer; text-decoration: underline;
}
.link-btn:hover:not(:disabled) { color: #0b2a66; }
.link-btn:disabled { color: #9ca3af; cursor: not-allowed; }
.link-btn.muted { color: #6b7280; font-weight: 500; }
.kv-error {
  width: 100%;
  font-size: 11px; color: #7E0F0F;
  margin-top: 2px;
}

.kv.kv-note { align-items: flex-start; }
.kv.kv-note .note-text {
  display: block;
  white-space: normal; word-break: break-word;
  font-style: italic; color: #4b5563;
}
.kv.kv-note .note-by {
  display: block; margin-top: 2px;
  font-style: normal; font-size: 11px; color: #9ca3af;
}

.muted { color: #9ca3af; }
.small { font-size: 13px; }

/* Drawer */
.drawer-scrim {
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(15, 23, 42, 0.4);
}
.drawer {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: 540px; max-width: calc(100vw - 64px);
  background: #fff;
  z-index: 10005;
  transform: translateX(100%);
  transition: transform 200ms ease;
  box-shadow: -8px 0 24px rgba(0,0,0,0.1);
  overflow-y: auto;
}
.drawer.open { transform: translateX(0); }
.drawer-content { padding: 16px 20px 32px; }
.drawer-header {
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid #eef1f5; padding-bottom: 12px; margin-bottom: 12px;
}
.drawer-header h2 { font-size: 18px; font-weight: 700; color: #061D47; margin: 0; }
.close-x {
  background: transparent; border: 0; cursor: pointer; font-size: 24px;
  color: #6b7280; line-height: 1; padding: 0 4px;
}
.close-x:hover { color: #1f2937; }

.detail-section {
  margin-top: 16px;
  padding: 12px 14px;
  background: #fafbfd;
  border-radius: 8px;
}
.detail-section h3 {
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: #6b7280; margin: 0 0 8px;
}
.kv {
  display: flex; justify-content: space-between; align-items: baseline;
  font-size: 13px; padding: 4px 0;
}
.kv > span:first-child { color: #6b7280; }
.kv > span:last-child {
  color: #1f2937; max-width: 60%; text-align: right; word-break: break-word;
}
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; }
.audit-list { list-style: none; padding: 0; margin: 0; }
.audit-list li { padding: 8px 0; border-bottom: 1px dashed #eef1f5; font-size: 12px; }
.audit-meta { display: flex; gap: 6px; align-items: center; color: #4b5563; flex-wrap: wrap; }
.audit-reason { color: #1f2937; margin-top: 2px; font-style: italic; }

/* Defense against any inherited pointer-events: none from the super-user
   overlay container or a Vuetify v-overlay that didn't fully tear down.
   Modal must always own clicks. */
.modal-overlay,
.modal-card,
.modal-card * { pointer-events: auto; }

/* Confirm-grant modal — shares the modal-overlay/card pattern with the
   other plain-HTML modals on this page. */
.modal-overlay {
  position: fixed; inset: 0; z-index: 10030;
  background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
}
.modal-card {
  background: #fff; border-radius: 12px;
  width: 720px; max-width: 100%;
  max-height: calc(100vh - 64px); overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.18);
}
.modal-card.small { width: 460px; }
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #eef1f5;
}
.modal-header h2 { margin: 0; font-size: 16px; font-weight: 700; color: #061D47; }
.modal-body { padding: 16px 20px; overflow-y: auto; flex: 1; }
.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 20px; border-top: 1px solid #eef1f5; background: #fafbfd;
}
.summary { list-style: none; padding: 0; margin: 0 0 8px; }
.summary li {
  display: flex; justify-content: space-between;
  padding: 6px 0; border-bottom: 1px dashed #eef1f5;
  font-size: 13px;
}
.summary li > span:first-child { color: #6b7280; }
.field { display: flex; flex-direction: column; gap: 4px; margin-top: 12px; }
.field span { font-size: 12px; color: #6b7280; }
.field textarea {
  width: 100%; box-sizing: border-box;
  padding: 8px 12px; border: 1px solid #c2cbe0; border-radius: 6px;
  font-size: 13px; font-family: inherit; resize: vertical; min-height: 60px;
}
.alert {
  margin-top: 12px;
  padding: 8px 12px; background: #fff5f5;
  border: 1px solid #fbd5d5; border-radius: 6px;
  color: #7E0F0F; font-size: 13px;
}

.modes {
  margin: 16px 0 4px;
  display: flex; flex-direction: column; gap: 10px;
}
.modes-label {
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.06em; text-transform: uppercase;
  color: #6b7280; margin-bottom: 6px;
}
.mode {
  display: flex; gap: 10px; align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid #eef1f5; border-radius: 8px;
  cursor: pointer; background: #fff;
}
.mode:hover { background: #fafbfd; }
.mode input[type="radio"] { margin-top: 4px; }
.mode-title { font-weight: 600; color: #1f2937; font-size: 14px; }
.mode-detail { color: #6b7280; font-size: 12px; margin-top: 2px; }
.custom-input-wrap {
  display: inline-flex; align-items: center; gap: 4px;
  margin-left: 8px;
}
.custom-prefix, .custom-suffix { color: #6b7280; font-size: 12px; }
.custom-input {
  width: 100px; padding: 4px 8px;
  border: 1px solid #c2cbe0; border-radius: 4px;
  font-size: 13px;
}
</style>

<style>
/* Google Places autocomplete dropdown is appended to document.body and
   isn't scoped — bump its z-index so it floats above the slide-out
   detail drawer (z-index ~10010). */
.pac-container {
  z-index: 10100 !important;
}
</style>
